import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Mail, CheckCircle2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { MODALITIES } from "@/constants/modalities";
import { EMAIL } from "@/constants/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";

const READY_ITEMS = [
  "Project state and location",
  "Current architectural or floor plan",
  "Equipment manufacturer and exact model",
  "Expected clinical use or workload",
  "Occupancy around the room — including above and below",
];

// Owner-written room-requirement guides cross-linked from their modality pages
const RELATED_ARTICLES = {
  "diagnostic-x-ray": { slug: "x-ray-room-shielding-requirements", title: "X-Ray Room Shielding Requirements: A Simple Guide" },
  ct: { slug: "ct-room-shielding-requirements", title: "CT Room Shielding Requirements: A Simple Guide" },
  "fluoroscopy-c-arms": { slug: "fluoroscopy-room-shielding-requirements", title: "Fluoroscopy Room Shielding Requirements: What You Need to Know" },
};

const ModalityPage = () => {
  const { slug } = useParams();
  const m = MODALITIES.find((x) => x.slug === slug);
  usePageMeta(
    m ? m.seoTitle : "Shielding Design Services | Heartland Physics",
    m ? m.seoDesc : "Physicist-prepared radiation shielding designs for diagnostic imaging facilities."
  );

  if (!m) {
    return (
      <section className="pt-16">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <p className="eyebrow">Not found</p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-ink">That service page doesn't exist.</h1>
          <Link to="/" data-testid="modality-404-home" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blueprint">
            Back to home <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    );
  }

  const siblings = MODALITIES.filter((x) => x.slug !== slug);

  return (
    <>
      <JsonLd
        id={`service-${m.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: `${m.name} Shielding Design`,
          description: m.short,
          provider: { "@type": "ProfessionalService", name: "Heartland Physics", url: "https://heartlandphysics.com/" },
          areaServed: "United States",
          offers: { "@type": "Offer", price: "950", priceCurrency: "USD" },
        }}
      />
      <section data-testid="modality-hero" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow" data-testid="modality-eyebrow">
              <Link to="/shielding-design" className="transition-colors hover:text-blueprint">Shielding Design</Link>
              {" — "}{m.name}
            </p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={m.headline} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="modality-intro">
              {m.intro}
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/request"
              data-testid="modality-cta-request"
              className="group flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Request a Shielding Design
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="modality-cta-email"
              className="flex items-center gap-2 border border-ink/25 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
            >
              <Mail size={16} /> Email Lancer@HeartlandPhysics.com
            </a>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-2">
          <div>
            <Reveal>
              <p className="eyebrow">Deliverable</p>
              <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink">What your report covers</h2>
            </Reveal>
            <div className="mt-8 border border-border">
              {m.covers.map((c, i) => (
                <Reveal key={c} delay={i * 0.05}>
                  <div className="flex items-start gap-4 border-b border-border p-4 last:border-b-0" data-testid={`modality-cover-${i}`}>
                    <span className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm leading-relaxed text-ink/75">{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <Reveal delay={0.1}>
              <p className="eyebrow">Projects</p>
              <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink">Typical {m.name.toLowerCase()} projects</h2>
            </Reveal>
            <div className="mt-8 flex flex-col gap-3">
              {m.projects.map((p, i) => (
                <Reveal key={p} delay={0.1 + i * 0.05}>
                  <div className="flex items-start gap-3 border border-border bg-paper p-4" data-testid={`modality-project-${i}`}>
                    <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-blueprint" />
                    <p className="text-sm leading-relaxed text-ink/75">{p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-blueprint-grid">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-2">
          <Reveal>
            <div className="border border-blueprint/30 bg-blueprint/5 p-8" data-testid="modality-note">
              <p className="eyebrow">Physicist's note</p>
              <p className="mt-4 text-base leading-relaxed text-ink/80">{m.note}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow">Prepare</p>
            <h2 className="mt-4 font-heading text-lg font-extrabold tracking-tight text-ink">What to have ready</h2>
            <div className="mt-8 flex flex-col gap-3">
              {READY_ITEMS.map((r, i) => (
                <div key={r} className="flex items-start gap-4 border-b border-border pb-3" data-testid={`modality-ready-${i}`}>
                  <span className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm leading-relaxed text-ink/75">{r}</p>
                </div>
              ))}
            </div>
            <a href={`mailto:${EMAIL}`} data-testid="modality-email-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blueprint transition-colors hover:text-ink">
              <Mail size={15} /> Email {EMAIL}
            </a>
            {RELATED_ARTICLES[m.slug] && (
              <Link
                to={`/insights/${RELATED_ARTICLES[m.slug].slug}`}
                data-testid="modality-article-link"
                className="group mt-5 block border border-border bg-white p-5 transition-colors duration-200 hover:border-blueprint"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">From the journal</p>
                <p className="mt-1.5 font-heading text-sm font-bold tracking-tight text-ink">{RELATED_ARTICLES[m.slug].title}</p>
                <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-blueprint">
                  Read the guide <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            )}
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <Reveal>
            <p className="eyebrow">Other modalities</p>
          </Reveal>
          <div className="mt-6 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {siblings.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06} className="h-full">
                <Link
                  to={`/shielding-design/${s.slug}`}
                  data-testid={`modality-sibling-${s.slug}`}
                  className="group flex h-full flex-col bg-white p-6 transition-colors duration-300 hover:bg-ink"
                >
                  <h3 className="font-heading text-sm font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-white">{s.name}</h3>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blueprint transition-colors duration-300 group-hover:text-azure">
                    Explore <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ModalityPage;
